/*
 * AUTO-GENERATED FILE — DO NOT EDIT.
 * Source of truth: packages/style-rules/src/
 * Regenerate with `pnpm generate:shared` (runs automatically at build/dev/install).
 */

/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import { z } from 'zod';

import {
  parseDateString,
  parseLocalDateString,
  parseTimeString,
} from './date-utils';

/**
 * A JSON-safe bigint literal. Native bigint values cannot be serialized as
 * JSON, so persisted style rules can use a signed decimal string instead.
 */
export const BigintLiteralSchema = z.object({
  bigint: z.string().regex(/^[+-]?\d+$/, {
    message: 'bigint must be a signed decimal integer string',
  }),
});
export type BigintLiteral = z.infer<typeof BigintLiteralSchema>;

/** A numeric style-rule literal, including native and JSON-safe bigints. */
export const NumericLiteralSchema = z.union([
  z.number(),
  z.bigint(),
  BigintLiteralSchema,
]);
export type NumericLiteral = z.infer<typeof NumericLiteralSchema>;

/**
 * An ISO 8601 or RFC 9557 datetime literal that resolves to a Unix millisecond
 * timestamp at evaluation time. Supported formats:
 * - RFC 9557 with IANA timezone: `"2024-06-15T14:00:00[Europe/Stockholm]"` (offset
 *   inferred from the named zone, DST-aware) or `"2024-06-15T14:00:00+02:00[Europe/Stockholm]"`
 *   (offset explicit)
 * - UTC or offset datetime: `"2024-01-01T00:00:00Z"`, `"2024-01-01T00:00:00+02:00"`
 * - Plain datetime (treated as UTC): `"2024-01-01T14:30:00"`, `"2024-01-01T14:30:00.500"`
 * - Date-only (treated as UTC midnight): `"2024-01-01"`
 */
export const DatetimeLiteralSchema = z.object({
  datetime: z.string().refine((input) => parseDateString(input) !== null, {
    message:
      'datetime must be a valid ISO 8601 / RFC 9557 date or datetime string',
  }),
});
export type DatetimeLiteral = z.infer<typeof DatetimeLiteralSchema>;

/**
 * A plain (unzoned) ISO 8601 date or datetime literal that resolves to a Unix
 * millisecond timestamp at evaluation time, interpreting the wall-clock time
 * in the browser's local timezone.
 *
 * Only plain date/datetime strings without timezone qualifiers are accepted:
 * - Plain datetime: `"2024-01-01T14:30:00"`, `"2024-01-01T14:30:00.500"`
 * - Date-only (treated as local midnight): `"2024-01-01"`
 *
 * Strings with `Z`, a UTC offset (`+02:00`), or an IANA bracket
 * (`[Europe/Stockholm]`) are **rejected** — use `{ datetime }` for
 * timezone-aware instants. This matches the semantics of Neo4j `LocalDateTime`.
 */
export const LocalDatetimeLiteralSchema = z.object({
  localdatetime: z
    .string()
    .refine((input) => parseLocalDateString(input) !== null, {
      message:
        'localdatetime must be a valid ISO 8601 plain date or datetime string (no Z, offset, or timezone)',
    }),
});
export type LocalDatetimeLiteral = z.infer<typeof LocalDatetimeLiteralSchema>;

/**
 * An ISO 8601 time literal that resolves to milliseconds since UTC midnight
 * at evaluation time. Supported formats:
 * - Local time (treated as UTC): `"14:30:00"`, `"14:30:00.500"`
 * - UTC time: `"14:30:00Z"` — identical to local form
 * - Offset time (UTC-normalised): `"14:30:00+02:00"` → 45,000,000 ms (12:30 UTC)
 *
 * IANA timezone brackets (e.g. `[Europe/Stockholm]`) are **not** accepted — a
 * time-of-day value has no calendar date so DST cannot be resolved. Use
 * `{ datetime }` literals for timezone-aware instants.
 *
 * Full datetime strings (containing `T`) are rejected.
 */
export const TimeLiteralSchema = z.object({
  time: z.string().refine((input) => parseTimeString(input) !== null, {
    message:
      'time must be a valid ISO 8601 time string (e.g. "14:30:00" or "14:30:00+02:00")',
  }),
});
export type TimeLiteral = z.infer<typeof TimeLiteralSchema>;

function isObjectWithStringKey<K extends string>(
  value: unknown,
  key: K,
): value is Record<K, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    key in value &&
    typeof (value as Record<string, unknown>)[key] === 'string'
  );
}

/** Returns whether a value is a `{ datetime: string }` wrapper. Does not validate the string. */
export function isDatetimeLiteral(value: unknown): value is DatetimeLiteral {
  return isObjectWithStringKey(value, 'datetime');
}

/** Returns whether a value is a `{ localdatetime: string }` wrapper. Does not validate the string. */
export function isLocalDatetimeLiteral(
  value: unknown,
): value is LocalDatetimeLiteral {
  return isObjectWithStringKey(value, 'localdatetime');
}

/** Returns whether a value is a `{ time: string }` wrapper. Does not validate the string. */
export function isTimeLiteral(value: unknown): value is TimeLiteral {
  return isObjectWithStringKey(value, 'time');
}

/**
 * A continuous, orderable literal that can be used as a range boundary.
 * Temporal literals resolve to numbers before range interpolation.
 */
export const RangeLiteralSchema = z.union([
  NumericLiteralSchema,
  DatetimeLiteralSchema,
  LocalDatetimeLiteralSchema,
  TimeLiteralSchema,
]);
export type RangeLiteral = z.infer<typeof RangeLiteralSchema>;
